import React, { FC, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { hockeyFormatter } from '@nhl/formatter-utilities';

import CommonMessages from '~/core/commonMessages';
import {
    ASSISTS_COL,
    GAMES_PLAYED_COL,
    GOALS_AGAINST_AVERAGE_COL,
    GOALS_COL,
    PLUS_MINUS_COL,
    POINTS_COL,
    SAVE_PCTG_COL,
    SHUTOUTS_COL,
    WINS_COL,
} from '~/core/tableColumns/playerTableColumns/columns';

import { FeaturedStats, SeasonObject } from '../../types';

import { FeaturedStatsBars } from './components/FeaturedStatsBars';

import {
    FlexCenter,
    PlayerFeaturedStatsContainer,
    StatsBarsContainer,
    StatsColumn,
    StatsToggle,
    ToggleContainer,
} from './styles';

type PlayerFeaturedStatsProps = {
    featuredStats: FeaturedStats;
    isGoalie: boolean;
    isPlayerInactive: boolean;
};

const FIRST_PANEL_KEY = 1;
const SECOND_PANEL_KEY = 2;

export const PlayerFeaturedStats: FC<PlayerFeaturedStatsProps> = ({ featuredStats, isGoalie, isPlayerInactive }) => {
    const intl = useIntl();
    const { season, regularSeason, playoffs } = featuredStats;

    const showCarousel = !!playoffs && !!regularSeason;

    const [selectedToggleKey, setSelectedToggleKey] = useState<number>(FIRST_PANEL_KEY);

    const formattedSeason = hockeyFormatter.singleSeasonFormatter(season);

    const toggleToFirstPanel = () => {
        handlePanelToggle(FIRST_PANEL_KEY);
    };

    const toggleToSecondPanel = () => {
        handlePanelToggle(SECOND_PANEL_KEY);
    };

    const handlePanelToggle = (key: number) => {
        if (key !== selectedToggleKey) {
            setSelectedToggleKey(key);
        }
    };

    const showSecondPanel = selectedToggleKey === SECOND_PANEL_KEY;

    const featuredStatsColumns = useMemo(() => {
        const colSize = 40;
        const maxSize = 122;

        const removeColumnSorting = (col) => ({
            ...col,
            enableSorting: false,
            meta: {
                ...col.meta,
                headerAriaLabel: undefined,
            },
        });

        const skaterCols = [
            GAMES_PLAYED_COL(intl),
            GOALS_COL(intl),
            ASSISTS_COL(intl),
            POINTS_COL(intl),
            PLUS_MINUS_COL(intl),
        ].map(removeColumnSorting);
        const goalieCols = [
            GAMES_PLAYED_COL(intl),
            WINS_COL(intl),
            SHUTOUTS_COL(intl),
            GOALS_AGAINST_AVERAGE_COL(intl),
            SAVE_PCTG_COL(intl),
        ].map(removeColumnSorting);

        const cols = isGoalie ? goalieCols : skaterCols;

        return cols.map((col) => ({
            ...col,
            size: colSize,
            minSize: colSize,
            maxSize: maxSize,
        }));
    }, [intl, isGoalie]);

    const firstFeaturedStats: SeasonObject = isPlayerInactive ? regularSeason : playoffs;
    const secondFeaturedStats: SeasonObject = isPlayerInactive ? playoffs : regularSeason;
    return showCarousel ? (
        <PlayerFeaturedStatsContainer>
            <StatsBarsContainer $toggled={showSecondPanel}>
                <StatsColumn>
                    <FeaturedStatsBars
                        id={isPlayerInactive ? 'regular-season' : 'playoffs'}
                        seasonStats={firstFeaturedStats?.subSeason}
                        careerStats={firstFeaturedStats?.career}
                        seasonStatsLabel={
                            <FormattedMessage
                                {...CommonMessages.tableHeaders.long[
                                    isPlayerInactive ? 'labeledSeason' : 'labeledPlayoffs'
                                ]}
                                values={{ formattedSeason }}
                            />
                        }
                        careerStatsLabel={
                            <FormattedMessage
                                {...CommonMessages.tableHeaders.long[isPlayerInactive ? 'career' : 'careerPlayoffs']}
                            />
                        }
                        columns={featuredStatsColumns}
                    />
                </StatsColumn>

                <StatsColumn>
                    <FeaturedStatsBars
                        id={isPlayerInactive ? 'playoffs' : 'regular-season'}
                        seasonStats={secondFeaturedStats?.subSeason}
                        careerStats={secondFeaturedStats?.career}
                        seasonStatsLabel={
                            <FormattedMessage
                                {...CommonMessages.tableHeaders.long[
                                    isPlayerInactive ? 'labeledPlayoffs' : 'labeledSeason'
                                ]}
                                values={{ formattedSeason }}
                            />
                        }
                        careerStatsLabel={
                            <FormattedMessage
                                {...CommonMessages.tableHeaders.long[isPlayerInactive ? 'careerPlayoffs' : 'career']}
                            />
                        }
                        columns={featuredStatsColumns}
                    />
                </StatsColumn>
            </StatsBarsContainer>

            <FlexCenter>
                <ToggleContainer>
                    <ChevronLeftIcon
                        className={`stats-chevron${showSecondPanel ? ' clickable' : ''}`}
                        onClick={() => toggleToFirstPanel()}
                    />
                    <StatsToggle onClick={() => handlePanelToggle(FIRST_PANEL_KEY)} $selected={!showSecondPanel} />
                    <StatsToggle onClick={() => handlePanelToggle(SECOND_PANEL_KEY)} $selected={showSecondPanel} />
                    <ChevronRightIcon
                        className={`stats-chevron${showSecondPanel ? '' : ' clickable'}`}
                        onClick={() => toggleToSecondPanel()}
                    />
                </ToggleContainer>
            </FlexCenter>
        </PlayerFeaturedStatsContainer>
    ) : (
        <PlayerFeaturedStatsContainer>
            <StatsBarsContainer $toggled={false}>
                <StatsColumn>
                    {regularSeason ? (
                        <FeaturedStatsBars
                            id="regular-season"
                            seasonStats={regularSeason?.subSeason}
                            careerStats={regularSeason?.career}
                            seasonStatsLabel={
                                <FormattedMessage
                                    {...CommonMessages.tableHeaders.long['labeledSeason']}
                                    values={{ formattedSeason }}
                                />
                            }
                            careerStatsLabel={<FormattedMessage {...CommonMessages.tableHeaders.long['career']} />}
                            columns={featuredStatsColumns}
                        />
                    ) : (
                        <StatsColumn>
                            <FeaturedStatsBars
                                id="playoffs"
                                seasonStats={playoffs?.subSeason}
                                careerStats={playoffs?.career}
                                seasonStatsLabel={
                                    <FormattedMessage
                                        {...CommonMessages.tableHeaders.long['labeledPlayoffs']}
                                        values={{ formattedSeason }}
                                    />
                                }
                                careerStatsLabel={
                                    <FormattedMessage {...CommonMessages.tableHeaders.long['careerPlayoffs']} />
                                }
                                columns={featuredStatsColumns}
                            />
                        </StatsColumn>
                    )}
                </StatsColumn>
            </StatsBarsContainer>

            <FlexCenter>
                <div />
            </FlexCenter>
        </PlayerFeaturedStatsContainer>
    );
};
